import { DefaultInput, Game, type JsonValue, NetplayPlayer } from 'netplayjs';
import { STAR_FIELD } from './constants';
import { mapDefaultInputToFrame } from './helpers';
import { MoonDuelSim } from './MoonDuelSim';
import type { CoinSimState, PlayerSimState, ProjectileState, SimState } from './types';

declare global {
  interface Window {
    __moonDuelDebug?: {
      instances: Array<{
        instanceId: number;
        frame: number;
        localPlayerId: number;
        requiredCoinsToLaunch: number;
        match: SimState['match'];
        players: Array<{
          id: number;
          x: number;
          y: number;
          health: number;
          maxHealth: number;
          jetpackFuel: number;
          maxJetpackFuel: number;
          coins: number;
        }>;
        projectileCount: number;
      }>;
    };
  }
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, '#05070f');
  gradient.addColorStop(1, '#101838');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  for (const star of STAR_FIELD) {
    const wrappedX = ((star.x % (width + star.size)) + width + star.size) % (width + star.size) - star.size;
    ctx.fillStyle = star.color;
    ctx.fillRect(wrappedX, star.y, star.size, star.size);
  }

  const earthX = width - 150;
  const earthY = 86;
  ctx.beginPath();
  ctx.arc(earthX, earthY, 44, 0, Math.PI * 2);
  ctx.fillStyle = '#456f9d';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(earthX - 12, earthY - 6, 14, 10, 0, 0, Math.PI * 2);
  ctx.ellipse(earthX + 10, earthY + 8, 16, 12, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#8db68a';
  ctx.fill();
}

function hash2D(x: number, y: number): number {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

function drawMoonRockPlatform(
  ctx: CanvasRenderingContext2D,
  platform: { x: number; y: number; width: number; height: number }
): void {
  const { x, y, width, height } = platform;

  const rock = ctx.createLinearGradient(0, y, 0, y + height);
  rock.addColorStop(0, '#717780');
  rock.addColorStop(0.45, '#646a73');
  rock.addColorStop(1, '#4f555f');
  ctx.fillStyle = rock;
  ctx.fillRect(x, y, width, height);

  ctx.fillStyle = '#8d949e';
  ctx.fillRect(x, y, width, Math.min(4, height));
  ctx.fillStyle = '#3d434b';
  ctx.fillRect(x, y + height - 2, width, 2);

  const speckleStep = 4;
  for (let py = y + 2; py < y + height - 2; py += speckleStep) {
    for (let px = x + 2; px < x + width - 2; px += speckleStep) {
      const n = hash2D(px, py) & 255;
      if (n > 226) {
        ctx.fillStyle = '#878e97';
        ctx.fillRect(px, py, 1, 1);
      } else if (n < 24) {
        ctx.fillStyle = '#3f454e';
        ctx.fillRect(px, py, 1, 1);
      }
    }
  }

  if (height >= 14) {
    for (let px = x + 12; px <= x + width - 12; px += 28) {
      const n = hash2D(px, y + height);
      const radius = 4 + (n % 5);
      const innerYOffsetRange = Math.max(1, height - 10);
      const cy = y + 6 + ((n >>> 3) % innerYOffsetRange);

      ctx.fillStyle = '#444a52';
      ctx.beginPath();
      ctx.arc(px, cy, radius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#6f757f';
      ctx.beginPath();
      ctx.arc(px - 1, cy - 1, Math.max(1, radius - 2), 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawPlatforms(ctx: CanvasRenderingContext2D, state: SimState): void {
  for (const platform of state.platforms) {
    drawMoonRockPlatform(ctx, platform);
  }
}

function drawGoalRocket(ctx: CanvasRenderingContext2D, state: SimState): void {
  const x = state.goal.x;
  const y = state.goal.y;
  const bodyX = x + 10;
  const bodyY = y + 12;
  const bodyW = 12;
  const bodyH = 34;
  const flamePulse = 2 + Math.abs(Math.trunc((state.frame % 12) - 6));

  ctx.fillStyle = '#d95555';
  ctx.beginPath();
  ctx.moveTo(bodyX, bodyY);
  ctx.lineTo(bodyX + bodyW, bodyY);
  ctx.lineTo(bodyX + Math.trunc(bodyW / 2), bodyY - 10);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#eceff8';
  ctx.fillRect(bodyX, bodyY, bodyW, bodyH);
  ctx.fillStyle = '#b7c1d6';
  ctx.fillRect(bodyX, bodyY + 6, bodyW, 3);

  ctx.fillStyle = '#7ab9e2';
  ctx.beginPath();
  ctx.arc(bodyX + Math.trunc(bodyW / 2), bodyY + 14, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#c74242';
  ctx.beginPath();
  ctx.moveTo(bodyX, bodyY + 24);
  ctx.lineTo(bodyX - 6, bodyY + 32);
  ctx.lineTo(bodyX, bodyY + 34);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(bodyX + bodyW, bodyY + 24);
  ctx.lineTo(bodyX + bodyW + 6, bodyY + 32);
  ctx.lineTo(bodyX + bodyW, bodyY + 34);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#6f778a';
  ctx.fillRect(bodyX + 3, bodyY + bodyH, 6, 3);

  const flameCenterX = bodyX + Math.trunc(bodyW / 2);
  const flameTopY = bodyY + bodyH + 3;
  ctx.fillStyle = '#ffd67a';
  ctx.beginPath();
  ctx.moveTo(flameCenterX - 5, flameTopY);
  ctx.lineTo(flameCenterX + 5, flameTopY);
  ctx.lineTo(flameCenterX, flameTopY + flamePulse + 6);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#ff8f3e';
  ctx.beginPath();
  ctx.moveTo(flameCenterX - 3, flameTopY);
  ctx.lineTo(flameCenterX + 3, flameTopY);
  ctx.lineTo(flameCenterX, flameTopY + flamePulse + 3);
  ctx.closePath();
  ctx.fill();
}

function drawCoin(ctx: CanvasRenderingContext2D, coin: CoinSimState): void {
  const cx = coin.x + Math.trunc(coin.width / 2);
  const cy = coin.y + Math.trunc(coin.height / 2);
  const rx = Math.max(6, Math.trunc(coin.width / 2) - 2);
  const ry = Math.max(6, Math.trunc(coin.height / 2) - 2);

  ctx.save();
  ctx.translate(cx, cy);

  ctx.beginPath();
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#ffd84d';
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-Math.trunc(rx / 6), -Math.trunc(ry / 6), Math.max(3, Math.trunc(rx / 2)), Math.max(3, Math.trunc(ry / 2)), 0, 0, Math.PI * 2);
  ctx.fillStyle = '#e2aa2c';
  ctx.fill();

  ctx.fillStyle = '#b97f1f';
  ctx.beginPath();
  ctx.ellipse(-Math.trunc(rx / 3), -Math.trunc(ry / 3), 2, 2, 0, 0, Math.PI * 2);
  ctx.ellipse(-Math.trunc(rx / 2), Math.trunc(ry / 4), 2, 2, 0, 0, Math.PI * 2);
  ctx.ellipse(Math.trunc(rx / 5), -1, 2, 1, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

function drawProjectiles(ctx: CanvasRenderingContext2D, projectiles: ProjectileState[]): void {
  for (const laser of projectiles) {
    const screenX = laser.x - Math.trunc(laser.width / 2);
    const screenY = laser.y - Math.trunc(laser.height / 2);
    ctx.fillStyle = '#ff3030';
    ctx.fillRect(screenX, screenY, laser.width, laser.height);
    ctx.fillStyle = '#ffd2a8';
    ctx.fillRect(screenX + 2, screenY + 1, Math.max(0, laser.width - 4), Math.max(1, laser.height - 2));
  }
}

function drawPlayer(ctx: CanvasRenderingContext2D, player: PlayerSimState): void {
  const primary = player.id === 0 ? '#84d9ff' : '#ff9aa5';
  const trim = player.id === 0 ? '#5ca9d1' : '#cf6f80';

  const screenX = player.x;
  const screenY = player.y;

  const backpackX = player.isFacingRight ? screenX + 2 : screenX + 24;
  ctx.fillStyle = '#9aa4b4';
  ctx.fillRect(backpackX, screenY + 12, 6, 12);

  ctx.fillStyle = primary;
  ctx.fillRect(screenX + 6, screenY + 10, 20, 16);

  ctx.fillStyle = '#f6f9ff';
  ctx.beginPath();
  ctx.arc(screenX + 16, screenY + 8, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = trim;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(screenX + 16, screenY + 8, 8, 0, Math.PI * 2);
  ctx.stroke();

  const visorShift = player.isFacingRight ? 2 : -2;
  ctx.fillStyle = '#6aa6cf';
  ctx.beginPath();
  ctx.arc(screenX + 16 + visorShift, screenY + 8, 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#cfd7e4';
  ctx.fillRect(screenX + 10, screenY + 26, 5, 6);
  ctx.fillRect(screenX + 17, screenY + 26, 5, 6);

  if (player.jetpackFuel > 0 && !player.isGrounded && player.velocityY < 0) {
    const flameX = player.isFacingRight ? screenX + 3 : screenX + 23;
    ctx.fillStyle = '#ff9f4d';
    ctx.fillRect(flameX, screenY + 24, 6, 7);
    ctx.fillStyle = '#ffe29c';
    ctx.fillRect(flameX + 1, screenY + 24, 4, 4);
  }

  const gunX = player.isFacingRight ? screenX + 24 : screenX - 8;
  const barrelX = player.isFacingRight ? gunX + 9 : gunX - 2;
  ctx.fillStyle = '#586070';
  ctx.fillRect(gunX, screenY + 14, 10, 4);
  ctx.fillStyle = '#ff5b5b';
  ctx.fillRect(barrelX, screenY + 15, 2, 2);
}

function drawMatchOverlay(ctx: CanvasRenderingContext2D, state: SimState): void {
  if (state.match.status === 'playing') return;

  ctx.fillStyle = '#00000088';
  ctx.fillRect(0, 0, state.stageWidth, state.stageHeight);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffd700';
  ctx.font = 'bold 42px sans-serif';

  if (state.match.status === 'draw') {
    ctx.fillText('Draw!', Math.trunc(state.stageWidth / 2), Math.trunc(state.stageHeight / 2) - 20);
  } else {
    const label = state.match.winnerId === 0 ? 'P1' : 'P2';
    const reason = state.match.winReason === 'rocket' ? 'Rocket Win' : 'Elimination';
    ctx.fillText(`${label} Wins`, Math.trunc(state.stageWidth / 2), Math.trunc(state.stageHeight / 2) - 20);
    ctx.fillStyle = '#ffffff';
    ctx.font = '24px sans-serif';
    ctx.fillText(reason, Math.trunc(state.stageWidth / 2), Math.trunc(state.stageHeight / 2) + 24);
  }

  ctx.textAlign = 'left';
}

export class MoonDuelNetplayGame extends Game {
  static timestep = 16;
  static canvasSize = { width: 800, height: 600 };
  static deterministic = true;

  private static nextInstanceId = 0;

  private readonly instanceId: number;
  private readonly players: NetplayPlayer[];
  private readonly localPlayerId: number;
  private readonly sim: MoonDuelSim;

  constructor(_canvas: HTMLCanvasElement, players: NetplayPlayer[]) {
    super();
    this.players = [...players].sort((a, b) => a.getID() - b.getID());
    this.localPlayerId = this.players.find((player) => player.isLocalPlayer())?.getID() ?? 0;
    this.sim = new MoonDuelSim();
    this.instanceId = MoonDuelNetplayGame.nextInstanceId++;
    this.publishDebug();
  }

  tick(playerInputs: Map<NetplayPlayer, DefaultInput>): void {
    const mappedInputs = new Map<number, ReturnType<typeof mapDefaultInputToFrame>>();
    for (const player of this.players) {
      mappedInputs.set(player.getID(), mapDefaultInputToFrame(playerInputs.get(player)));
    }
    this.sim.tick(mappedInputs);
    this.publishDebug();
  }

  draw(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const state = this.sim.getState();
    drawBackground(ctx, state.stageWidth, state.stageHeight);
    drawPlatforms(ctx, state);
    drawGoalRocket(ctx, state);

    for (const coin of state.coins) {
      if (!coin.collected) drawCoin(ctx, coin);
    }

    drawProjectiles(ctx, state.projectiles);
    for (const player of state.players) {
      drawPlayer(ctx, player);
    }

    drawMatchOverlay(ctx, state);
  }

  serialize(): JsonValue {
    return this.sim.serialize() as unknown as JsonValue;
  }

  deserialize(value: JsonValue): void {
    this.sim.deserialize(value as unknown as SimState);
    this.publishDebug();
  }

  private publishDebug(): void {
    if (typeof window === 'undefined') return;
    const snapshot = this.sim.getState();
    const entry = {
      instanceId: this.instanceId,
      frame: snapshot.frame,
      localPlayerId: this.localPlayerId,
      requiredCoinsToLaunch: snapshot.requiredCoinsToLaunch,
      match: snapshot.match,
      players: snapshot.players.map((player) => ({
        id: player.id,
        x: player.x,
        y: player.y,
        health: player.health,
        maxHealth: player.maxHealth,
        jetpackFuel: player.jetpackFuel,
        maxJetpackFuel: player.maxJetpackFuel,
        coins: player.coinCount,
      })),
      projectileCount: snapshot.projectiles.length,
    };

    if (!window.__moonDuelDebug) {
      window.__moonDuelDebug = { instances: [] };
    }

    window.__moonDuelDebug.instances[this.instanceId] = entry;
  }
}
